import { useEffect, useState } from "react";
import { Check, CreditCard, Smartphone, Trash2, Send, Info } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtNum } from "@/lib/format";
import { LIMIT_KEYS, PLAN_KEYS, STATUS_TONE, daysLeft } from "@/lib/plans";
import { BigButton, Chip, Field, PageTitle, inputCls } from "@/components/ui-kit";
import { MyInvoices } from "@/components/billing/MyInvoices";

const card = "rounded-xl bg-white border border-border p-5 card-soft";

function StatusCard({ b }) {
  const { t, lang } = useApp();
  return (
    <div className={card} data-testid="billing-status-card">
      <div className="text-sm text-muted-foreground">{t("current_plan")}</div>
      <div className="flex flex-wrap items-center gap-3 mt-1">
        <span className="font-heading text-3xl" data-testid="billing-effective-plan">{t(`plan_${b.effective}`)}</span>
        <span className="px-2.5 py-1 rounded-full text-xs font-bold text-white" style={{ background: STATUS_TONE[b.status] }} data-testid="billing-status">{t(`plan_status_${b.status}`)}</span>
      </div>
      <div className="mt-2 text-sm font-semibold text-muted-foreground" data-testid="billing-status-detail">
        {b.status === "trial" && t("trial_days_left", { n: daysLeft(b.trial_ends) })}
        {b.status === "active" && t("plan_expires_on", { d: fmtDate(b.plan_expires, lang) })}
        {b.status === "expired" && t("trial_over")}
      </div>
    </div>
  );
}

function Usage({ b }) {
  const { t } = useApp();
  return (
    <div className={`${card} space-y-3`} data-testid="billing-usage">
      <h3 className="font-heading text-lg">{t("usage")}</h3>
      {LIMIT_KEYS.map((k) => {
        const lim = b.limits[k];
        const pct = lim ? Math.min(100, (b.usage[k] / lim) * 100) : 8;
        return (
          <div key={k} data-testid={`billing-usage-${k}`}>
            <div className="flex justify-between text-sm font-bold"><span>{t(`limit_${k}`)}</span><span dir="ltr">{b.usage[k]} / {lim ?? "∞"}</span></div>
            <div className="h-2.5 rounded-full bg-secondary mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct >= 100 ? "#D93838" : "#0B2A5B" }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function PlanCard({ k, b, onRequest }) {
  const { t } = useApp();
  const price = b.settings.prices?.[k];
  const current = b.effective === k;
  return (
    <div className={`${card} flex flex-col gap-3 ${current ? "ring-2 ring-[#0B2A5B]" : ""}`} data-testid={`billing-plan-${k}`}>
      <div className="font-heading text-2xl">{t(`plan_${k}`)}</div>
      <div className="text-xl font-extrabold" data-testid={`billing-price-${k}`}>
        {k === "free" ? t("plan_free") : price != null ? `${fmtNum(price)} ${b.settings.currency || ""}` : t("price_tbd")}
        {k !== "free" && price != null && <span className="text-sm font-semibold text-muted-foreground">{t("per_month")}</span>}
      </div>
      <ul className="space-y-1.5 text-sm flex-1">
        {LIMIT_KEYS.map((l) => <li key={l} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#1F9D55]" />{t(`limit_${l}`)}: <b>{b.plans[k][l] ?? t("unlimited")}</b></li>)}
      </ul>
      {k !== "free" && <BigButton variant={current ? "soft" : "primary"} onClick={() => onRequest(k)} data-testid={`billing-request-${k}`}><Send className="w-5 h-5" />{t("request_plan")}</BigButton>}
    </div>
  );
}

function RequestDialog({ plan, onClose, onSent }) {
  const { t } = useApp();
  const [f, setF] = useState({ months: 1, method: "cash", reference: "" });
  useEffect(() => { if (plan) setF({ months: 1, method: "cash", reference: "" }); }, [plan]);
  const send = async () => {
    try { await api.post("/billing/request", { plan, ...f, months: Number(f.months) }); toast.success(t("request_sent")); onSent(); }
    catch (e) { const m = errMsg(e); toast.error(m === "request_pending" ? t(m) : m); }
  };
  return (
    <Dialog open={!!plan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="billing-request-dialog">
        <DialogTitle className="font-heading text-2xl">{t("request_plan")}: {plan && t(`plan_${plan}`)}</DialogTitle>
        <Field label={t("months")}>
          <div className="flex gap-2">{[1, 3, 6, 12].map((m) => <Chip key={m} active={Number(f.months) === m} onClick={() => setF({ ...f, months: m })} data-testid={`billing-months-${m}`}>{m}</Chip>)}</div>
        </Field>
        <Field label={t("pay_method")}>
          <div className="flex gap-2">{["cash", "transfer"].map((m) => <Chip key={m} active={f.method === m} onClick={() => setF({ ...f, method: m })} data-testid={`billing-method-${m}`}>{t(`method_${m}`)}</Chip>)}</div>
        </Field>
        <Field label={t("pay_reference")}><input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className={inputCls} maxLength={120} data-testid="billing-reference-input" /></Field>
        <BigButton onClick={send} data-testid="billing-send-request"><Send className="w-5 h-5" />{t("send_request")}</BigButton>
      </DialogContent>
    </Dialog>
  );
}

function Devices({ b, onRemove }) {
  const { t, lang } = useApp();
  const mine = localStorage.getItem("sp_device");
  return (
    <div className={`${card} space-y-2`} data-testid="billing-devices">
      <h3 className="font-heading text-lg flex items-center gap-2"><Smartphone className="w-5 h-5" />{t("devices_title")}</h3>
      {b.devices.map((d) => (
        <div key={d.id} className="flex items-center gap-3 border-t border-border pt-2 text-sm" data-testid="billing-device-row">
          <span className="font-mono text-xs flex-1 truncate" dir="ltr">{d.id.slice(0, 8)}{d.id === mine && <b className="ms-2 font-sans text-[#1F9D55]">{t("this_device")}</b>}</span>
          <span className="text-muted-foreground">{t("last_seen")}: {fmtDate(d.last_seen, lang)}</span>
          {d.id !== mine && <button type="button" onClick={() => onRemove(d.id)} className="h-9 px-3 rounded-lg bg-destructive/10 text-destructive font-bold flex items-center gap-1" data-testid="billing-device-remove"><Trash2 className="w-4 h-4" />{t("remove")}</button>}
        </div>
      ))}
    </div>
  );
}

function Requests({ b }) {
  const { t, lang } = useApp();
  if (!b.requests.length) return null;
  return (
    <div className={`${card} overflow-x-auto`} data-testid="billing-requests">
      <h3 className="font-heading text-lg mb-2">{t("plan_requests")}</h3>
      <table className="w-full text-sm">
        <tbody>
          {b.requests.map((r) => (
            <tr key={r.id} className="border-t border-border" data-testid="billing-request-row">
              <td className="py-2">{fmtDate(r.created_at, lang)}</td><td className="py-2 font-bold">{t(`plan_${r.plan}`)} × {r.months}</td>
              <td className="py-2">{t(`method_${r.method}`)}</td><td className="py-2 text-muted-foreground">{r.reference}</td>
              <td className="py-2"><span className="px-2 py-0.5 rounded-full bg-secondary text-xs font-bold" data-testid="billing-request-status">{t(`req_${r.status}`)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Billing() {
  const { t } = useApp();
  const [b, setB] = useState(null);
  const [req, setReq] = useState(null);
  const load = () => api.get("/billing").then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { try { await api.delete(`/billing/devices/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!b) return <p className="p-4">{t("loading")}</p>;
  return (
    <div className="space-y-4" data-testid="billing-page">
      <PageTitle testid="billing-title">{t("billing")}</PageTitle>
      <div className="grid lg:grid-cols-2 gap-4"><StatusCard b={b} /><Usage b={b} /></div>
      <MyInvoices />
      <div className="grid md:grid-cols-3 gap-4">{PLAN_KEYS.map((k) => <PlanCard key={k} k={k} b={b} onRequest={setReq} />)}</div>
      <div className={`${card} flex gap-3`} data-testid="billing-instructions">
        <Info className="w-5 h-5 shrink-0 text-[#2B79C2]" />
        <div><div className="font-bold flex items-center gap-2"><CreditCard className="w-4 h-4" />{t("how_to_pay")}</div>
          <p className="text-sm whitespace-pre-line text-muted-foreground mt-1">{b.settings.instructions || t("pay_instructions_default")}</p></div>
      </div>
      <Requests b={b} />
      <Devices b={b} onRemove={remove} />
      <RequestDialog plan={req} onClose={() => setReq(null)} onSent={() => { setReq(null); load(); }} />
    </div>
  );
}
